import { defineStore } from 'pinia';
import type {
  MissionV1 as MissionV1,
  ListMissionsQueryParams
} from '~/api/v1/missions/MissionRepository';

import type {
  CreateMissionPayload,
  MissionResponse, UpdateMissionPayload, GetMissionQueryParams,
  GetMatchingUsersParams, MatchingUserResponse,
} from '~/api/v2/missions/MissionRepository';
import { authStore } from '~/stores/auth';

// Draft persistence constants
const DRAFT_STORAGE_KEY_PREFIX = 'big:mission:draft';
const DRAFT_EXPIRY_DAYS = 7;
const DRAFT_VERSION = 1;

/**
 * Get the draft storage key scoped to the current user.
 * Falls back to the generic key if no user is authenticated.
 */
function getDraftStorageKey(): string {
  try {
    const auth = authStore();
    const userId = auth.user?.id;
    if (userId) {
      return `${DRAFT_STORAGE_KEY_PREFIX}:${userId}`;
    }
  } catch {
    // authStore may not be available during SSR or early init
  }
  return DRAFT_STORAGE_KEY_PREFIX;
}

export interface MissionDraft {
  version: number;
  currentStep: number;
  step1Data: Record<string, any>;
  step2Data: Record<string, any>;
  step3Data: Record<string, any>;
  step4Data: Record<string, any>;
  step5Data: Record<string, any>;
  lastUpdated: number;
}

/**
 * Check if draft is expired (older than DRAFT_EXPIRY_DAYS)
 */
function isDraftExpired(draft: MissionDraft): boolean {
  const expiryMs = DRAFT_EXPIRY_DAYS * 24 * 60 * 60 * 1000;
  const now = Date.now();
  return (now - draft.lastUpdated) > expiryMs;
}

/**
 * Load draft from localStorage with expiry and version checks
 */
function loadDraftFromStorage(): MissionDraft | null {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    const key = getDraftStorageKey();
    const stored = localStorage.getItem(key);

    // Also try to clean up any legacy unscoped draft
    if (stored === null) {
      migrateLegacyDraft(key);
      const migrated = localStorage.getItem(key);
      if (migrated === null) return null;
      return parseDraft(key, migrated);
    }

    return parseDraft(key, stored);
  } catch {
    return null;
  }
}

function parseDraft(key: string, stored: string): MissionDraft | null {
  try {
    const draft = JSON.parse(stored) as MissionDraft;

    if (draft.version !== DRAFT_VERSION) {
      localStorage.removeItem(key);
      return null;
    }

    if (isDraftExpired(draft)) {
      localStorage.removeItem(key);
      return null;
    }

    return draft;
  } catch {
    try { localStorage.removeItem(key); } catch { /* ignore */ }
    return null;
  }
}

/**
 * Migrate a legacy draft (stored without user ID) to the user-scoped key.
 * This ensures existing drafts are picked up after the fix, then the old key is removed.
 */
function migrateLegacyDraft(scopedKey: string): void {
  if (scopedKey === DRAFT_STORAGE_KEY_PREFIX) return; // No user ID, can't migrate
  try {
    const legacy = localStorage.getItem(DRAFT_STORAGE_KEY_PREFIX);
    if (legacy) {
      localStorage.setItem(scopedKey, legacy);
      localStorage.removeItem(DRAFT_STORAGE_KEY_PREFIX);
    }
  } catch { /* ignore */ }
}

/**
 * Save draft to localStorage with error handling
 */
function saveDraftToStorage(draft: MissionDraft): void {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    localStorage.setItem(getDraftStorageKey(), JSON.stringify(draft));
  } catch {
    console.warn('Failed to save mission draft to localStorage');
  }
}

/**
 * Remove draft from localStorage
 */
function removeDraftFromStorage(): void {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    localStorage.removeItem(getDraftStorageKey());
  } catch {
    // Ignore removal errors
  }
}

export const missionStore = defineStore('mission', {
  state: () => ({
    missions: [] as MissionV1[],
    mission: null as MissionResponse | null,
    totalMissions: 0,
    isLoading: false,
    error: null as string | null,
    pagination: null,
    draft: loadDraftFromStorage(),
  }),

  getters: {
    getMissions: (state) => state.missions,
    getTotalMissions: (state) => state.totalMissions,
    hasDraft: (state) => state.draft !== null,
    getDraft: (state) => state.draft,
  },

  actions: {
    async fetchMissions(params: ListMissionsQueryParams = {}) {
      this.missions = [];
      try {
        this.isLoading = true;
        this.error = null;

        const { $api } = useNuxtApp();
        const response = await $api().v2.missions.list(params);

        this.missions = response.data;
        this.totalMissions = response.total;

        return response;
      } catch (error) {
        console.error('Error fetching missions:', error);
        this.error = error instanceof Error ? error.message : 'Failed to fetch missions';
        throw error;
      } finally {
        this.isLoading = false;
      }
    },

    async fetchMissionsInfinite(params?: any) {
      const { $api } = useNuxtApp();
      return $api().v2.missions.list(params).then((response) => {
        // Ajouter les nouvelles missions à la liste existante
        this.missions = [...this.missions, ...response.data];
        this.pagination = response;
        return response;
      });
    },

    createMission(payload: CreateMissionPayload, companyId?: string, options: { skipGlobalErrorAlert?: boolean } = {}) {
      const { $api } = useNuxtApp();
      return $api().v2.missions.create(payload, companyId, options).then((response: MissionResponse) => {

        this.mission = response
        this.missions.unshift(response);

        return response
      })
    },

    updateMission(mission_id: string, payload: UpdateMissionPayload = {}) {
      const { $api } = useNuxtApp();

      return $api().v2.missions.update(mission_id, payload).then((response: MissionResponse) => {

        this.mission = response

        return response
      })
    },

    async fetchOneMission(missionId: string, params: GetMissionQueryParams = {}) {
      const { $api } = useNuxtApp();
      return $api().v2.missions.getOneById(missionId, params).then((response: MissionResponse) => {
          this.mission = response;
          return response
      });
    },

    async deleteMission(missionId: string) {
      try {
        this.isLoading = true;
        this.error = null;

        const { $api } = useNuxtApp();
        await $api().v1.missions.deleteMission(missionId);

        // Remove mission from store after successful deletion
        this.missions = this.missions.filter(mission => mission.mission_id !== missionId);

        return true;
      } catch (error) {
        console.error(`Error deleting mission ${missionId}:`, error);
        this.error = error instanceof Error ? error.message : `Failed to delete mission ${missionId}`;
        throw error;
      } finally {
        this.isLoading = false;
      }
    },

    // Draft management actions
    saveDraft(draftData: Omit<MissionDraft, 'lastUpdated' | 'version'>) {
      const draft: MissionDraft = {
        ...draftData,
        version: DRAFT_VERSION,
        lastUpdated: Date.now(),
      };
      this.draft = draft;
      saveDraftToStorage(draft);
    },

    clearDraft() {
      this.draft = null;
      removeDraftFromStorage();
    },

    async fetchMatchingUsers(missionId: string, params: GetMatchingUsersParams = {}): Promise<MatchingUserResponse[]> {
      try {
        const { $api } = useNuxtApp();
        return await $api().v2.missions.getMatchingUsers(missionId, params);
      } catch (error) {
        console.error('Error fetching matching users:', error);
        throw error;
      }
    },

    markMissionAsApplied(missionId: string) {
      const index = this.missions.findIndex(m => m.mission_id === missionId || (m as any).id === missionId);
      if (index !== -1) {
        this.missions[index] = { ...this.missions[index], has_applied: true, application_status: 'applied' };
      }
      if (this.mission && ((this.mission as any).id === missionId || (this.mission as any).mission_id === missionId)) {
        this.mission = { ...this.mission, has_applied: true, application_status: 'applied' };
      }
    },
  },
});
